import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  selectedBranchAtom,
  searchValuesAtom,
  currentDateAtom,
  reloadAtom,
  subsectionsAtom,
  allOutcomesAtom,
} from '../../Atoms';
import {
  CALL_STANDARD_FILTERS,
  STANDARD_SORTING_OPTIONS,
} from '../../Helpers/Filters/constants';
import mapFilters from '../../Helpers/Filters/mapFilters';
import saveFiltersInLocal from '../../Helpers/Filters/saveFiltersInLocal';
import useInfiniteQuery from '../../Hooks/useInfiniteQuery';
import { download } from '../../api/utils';
import useAPI from '../../api';
import SectionPage from './SectionPage';
import ErrorDialog from '../Error';
import { formatWavePeriodValue } from '../../Utils/constants/general';
import useAbortEffect from '../../Hooks/useAbortEffect';
import formatCallStats from '../../Helpers/formatCallStats';
import formatWaveStats from '../../Helpers/formatWaveStats';
import { checkFeature } from '../../Router/routes';

/**
 * Route customers component wrapper with api calls:
 *
 * @param {Object} props - Object. Component's props.
 * @param {Node} props.icon - ReactElement. Section's icon for stats box.
 * @param {Boolean} props.tablet - Boolean. Media query for width.
 * @param {Array} props.userRoles - Array. Array containing logged user's roles.
 * @param {Boolean} props.heightQuery - Boolean. Media query for height.
 * @param {String} props.sectionName - String. SectionName string displayed and used for API calls.
 * @param {Array} props.defaultRating - Array. Array of numbers containing the default  selected rating when loading the page.
 * @param {String} props.period - (Optional) String. Represents the api call period in days.
 * @param {String} props.wavePeriod - (Optional) String. Wave period param for api calls.
 * @param {String} props.apiType - String. Represents the API call type to pass to fetch's route request.
 * @param {Boolean} props.noStats - Boolean. Flag indicating that the section doesn't have stats.
 *
 * @returns {React.JSX.Element}
 */

const PageWrapper = function PageWrapper({
  statsTabs = [],
  sectionName,
  fab,
  fabVariant,
  onClickFab,
  tablet = true,
  heightQuery = true,
  // defaultRating = [],
  period = null,
  apiType = 'SinceLastCall',
  noStats = checkFeature('HIDE_DAILY_STATS'),
  searchBoxFilters = ['fullNameSearch', 'fiscalCode', 'mobilePhoneNumber'],
  standardFilters = CALL_STANDARD_FILTERS,
  rangeFilters = [{ keyName: 'distanceKm', defaultValue: null }],
  wavePeriod = formatWavePeriodValue(),
  sortingOptions = STANDARD_SORTING_OPTIONS,
  columns = [],
  sectionSubName,
  customStatusChipCallback,
  ...props
}) {
  const intl = useIntl();
  const API = useAPI();
  const theme = useTheme();
  const location = useLocation()?.pathname;

  const allOutcomes = useAtomValue(allOutcomesAtom);
  const setSubsections = useSetAtom(subsectionsAtom);
  const [reloadCustomers, setReload] = useAtom(reloadAtom);
  const searchValues = useAtomValue(searchValuesAtom);
  const { id: branchId, branchDescription: branchName } =
    useAtomValue(selectedBranchAtom) || {};
  const currentDate = useAtomValue(currentDateAtom);
  const [stats, setStats] = useState(null);
  const [statsTabValue, setStatsTabValue] = useState(0);

  useEffect(() => {
    if (sectionName)
      API.getSubsections(
        sectionName,
        { silent: true },
        (res) => {
          setSubsections(res);
        },
        () => {
          setSubsections([]);
        }
      );
  }, [sectionName]);

  const downloadCustomersFile = useCallback(
    () =>
      download(
        `${sectionName}-${branchName}`,
        API.export2Excel(
          branchId,
          {
            type: apiType,
            period: period !== null ? period : currentDate.toISOString(),
            sectionName,
            wavePeriod,
          },
          {
            pageNumber: 0,
            filterExpression: mapFilters(
              searchBoxFilters,
              standardFilters,
              rangeFilters,
              searchValues
            ),
            sortExpression: searchValues?.sortValue
              ? {
                  sortValue: searchValues?.sortValue.value,
                  sort: searchValues?.sortValue.direction,
                }
              : null,
          }
        )
      ),
    [branchId, searchValues, wavePeriod]
  );

  const getCustomers = useCallback(
    (pageNumber) => {
      return branchId
        ? API.getCustomers(
            branchId,
            {
              type: apiType,
              period: period !== null ? period : currentDate.toISOString(),
              sectionName: sectionName === 'WELCOME' ? null : sectionName,
              wavePeriod: sectionName === 'SPOT' ? wavePeriod : '',
            },
            {
              pageNumber,
              filterExpression: mapFilters(
                searchBoxFilters,
                standardFilters,
                rangeFilters,
                searchValues
              ),
              sortExpression: searchValues?.sortValue
                ? {
                    sortValue: searchValues?.sortValue.value,
                    sort: searchValues?.sortValue.direction,
                  }
                : null,
              silent: pageNumber > 1,
            },
            () => {
              saveFiltersInLocal(searchValues, location);
              setReload(false);
            },
            (err) => {
              console.log(err);
              setReload(false);
            }
          )
        : Promise.resolve([]);
    },
    [currentDate, wavePeriod, searchValues, branchId]
  );
  const getSectionStats = (signal, tabValue) => {
    if (branchId && !noStats && allOutcomes.length > 0) {
      setStats(null);
      const statsPeriod = statsTabs[tabValue];
      API.getStats(
        branchId,
        statsPeriod,
        sectionName === 'BIRTHDAYS' ? apiType : sectionName,
        wavePeriod,
        { silent: true, signal },
        (result) => {
          if (statsTabs[tabValue] === 'WAVE') {
            setStats(formatWaveStats(result, intl, theme));
          } else {
            setStats(formatCallStats(result, intl, allOutcomes));
          }
        },
        () => {
          setStats({});
        }
      );
    }
  };

  const getTodayCalls = useCallback(
    (pageNumber) => {
      return branchId
        ? API.todayCalls(branchId, {
            pageNumber,
            silent: true,
          })
        : Promise.resolve([]);
    },
    [branchId]
  );

  const {
    items: contacts,
    error,
    ObserverWrapper,
    removeClickedItem,
    recordCount,
    updateItem,
    reload,
  } = useInfiniteQuery(getCustomers);

  useEffect(() => {
    if (reloadCustomers) {
      reload();
    }
  }, [reloadCustomers]);

  const { items: calls, ObserverWrapper: ObserverWrapperActivities } =
    useInfiniteQuery(getTodayCalls);

  useAbortEffect(
    (signal) => getSectionStats(signal, statsTabValue),
    [noStats, wavePeriod, branchId, sectionName, statsTabValue, allOutcomes]
  );

  const handleChangeStatsTabValue = (event, newValue) => {
    setStatsTabValue(newValue);
  };

  return (
    <>
      {error && <ErrorDialog error={error} />}
      <SectionPage
        customStatusChipCallback={customStatusChipCallback}
        columns={columns}
        stats={stats}
        tabs={statsTabs}
        tabValue={statsTabValue}
        onChangeTabValue={handleChangeStatsTabValue}
        tablet={tablet}
        hideStats={noStats}
        updateItem={updateItem}
        sectionName={sectionName}
        heightQuery={heightQuery}
        filters={[
          ...rangeFilters,
          ...standardFilters.map((x) => ({
            ...x,
            // defaultValue: x?.keyName === 'ratingValue' && defaultRating,
          })),
        ]}
        sortingOptions={sortingOptions}
        // defaultRating={defaultRating}
        contactsCount={contacts.length}
        activitiesCount={calls?.length}
        ExportToExcel={downloadCustomersFile}
        ListObserverWrapper={ObserverWrapper}
        mainBoxWidth={noStats ? '100%' : '70%'}
        StatsObserverWrapper={ObserverWrapperActivities}
        removeFromList={(id) => removeClickedItem(id, recordCount - 1)}
        wavePeriod={wavePeriod}
        sectionSubName={sectionSubName}
        fab={fab}
        fabVariant={fabVariant}
        onClickFab={onClickFab}
        {...props}
      />
    </>
  );
};

PageWrapper.propTypes = {
  tablet: PropTypes.bool,
  userRoles: PropTypes.array,
  heightQuery: PropTypes.bool,
  // defaultRating: PropTypes.array,
  period: PropTypes.number,
  apiType: PropTypes.string,
  sectionName: PropTypes.string.isRequired,
  noStats: PropTypes.bool,
  searchBoxFilters: PropTypes.array,
  standardFilters: PropTypes.array,
  rangeFilters: PropTypes.array,
  wavePeriod: PropTypes.string,
  sortingOptions: PropTypes.array,
  statsTabs: PropTypes.array,
  fab: PropTypes.bool,
  fabVariant: PropTypes.string,
  onClickFab: PropTypes.func,
};

export default PageWrapper;
